import {
  AlertTriangle,
  CheckSquare,
  Crosshair,
  Globe,
  MapPin,
  Search,
  Square,
  Trash2,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAds } from "../../hooks/AdsContext";

const BR_STATES = [
  { key: "BR-AC", name: "Acre", lat: -9.024, lon: -70.812, zoom: 7 },
  { key: "BR-AL", name: "Alagoas", lat: -9.571, lon: -36.782, zoom: 8 },
  { key: "BR-AM", name: "Amazonas", lat: -3.417, lon: -65.856, zoom: 5 },
  { key: "BR-AP", name: "Amapá", lat: 1.41, lon: -51.77, zoom: 7 },
  { key: "BR-BA", name: "Bahia", lat: -12.58, lon: -41.7, zoom: 6 },
  { key: "BR-CE", name: "Ceará", lat: -5.498, lon: -39.32, zoom: 7 },
  {
    key: "BR-DF",
    name: "Distrito Federal",
    lat: -15.8,
    lon: -47.864,
    zoom: 10,
  },
  { key: "BR-ES", name: "Espírito Santo", lat: -19.183, lon: -40.308, zoom: 8 },
  { key: "BR-GO", name: "Goiás", lat: -15.827, lon: -49.836, zoom: 7 },
  { key: "BR-MA", name: "Maranhão", lat: -5.422, lon: -45.844, zoom: 7 },
  { key: "BR-MG", name: "Minas Gerais", lat: -18.512, lon: -44.555, zoom: 6 },
  {
    key: "BR-MS",
    name: "Mato Grosso do Sul",
    lat: -20.772,
    lon: -54.785,
    zoom: 7,
  },
  { key: "BR-MT", name: "Mato Grosso", lat: -12.682, lon: -56.921, zoom: 6 },
  { key: "BR-PA", name: "Pará", lat: -3.417, lon: -52.333, zoom: 5 },
  { key: "BR-PB", name: "Paraíba", lat: -7.24, lon: -36.782, zoom: 8 },
  { key: "BR-PE", name: "Pernambuco", lat: -8.814, lon: -36.954, zoom: 7 },
  { key: "BR-PI", name: "Piauí", lat: -7.718, lon: -42.728, zoom: 7 },
  { key: "BR-PR", name: "Paraná", lat: -24.895, lon: -51.55, zoom: 7 },
  { key: "BR-RJ", name: "Rio de Janeiro", lat: -22.386, lon: -43.0, zoom: 8 },
  {
    key: "BR-RN",
    name: "Rio Grande do Norte",
    lat: -5.813,
    lon: -36.204,
    zoom: 8,
  },
  { key: "BR-RO", name: "Rondônia", lat: -10.838, lon: -62.998, zoom: 7 },
  { key: "BR-RR", name: "Roraima", lat: 2.738, lon: -62.075, zoom: 6 },
  {
    key: "BR-RS",
    name: "Rio Grande do Sul",
    lat: -30.035,
    lon: -51.217,
    zoom: 7,
  },
  { key: "BR-SC", name: "Santa Catarina", lat: -27.242, lon: -50.218, zoom: 8 },
  { key: "BR-SE", name: "Sergipe", lat: -10.574, lon: -37.385, zoom: 9 },
  { key: "BR-SP", name: "São Paulo", lat: -22.0, lon: -48.5, zoom: 7 },
  { key: "BR-TO", name: "Tocantins", lat: -10.175, lon: -48.298, zoom: 7 },
];

const GeolocationCenter: React.FC = () => {
  const {
    customLocations,
    setCustomLocations,
    radiusKm,
    setRadiusKm,
    handleGeocodeSearch,
    searchTerms,
    setSearchTerms,
    geocodeResults,
    setGeocodeResults,
    states,
    setStates,
    setCountry,
  } = useAds();
  const { t } = useTranslation();

  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [leafletReady, setLeafletReady] = useState(false);
  const [locationMode, setLocationMode] = useState<"radius" | "state">(
    states.length > 0 ? "state" : "radius",
  );
  const [targetBrasil, setTargetBrasil] = useState(
    states.length === 0 && customLocations.length === 0,
  );
  const [stateFilter, setStateFilter] = useState("");
  // Touch devices: map starts locked (scroll-safe) until user explicitly activates it
  const [mapInteractive, setMapInteractive] = useState(false);
  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  // Carregar CSS e Script do Leaflet se não existirem
  useEffect(() => {
    if (!(window as any).L) {
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }

      if (!document.getElementById("leaflet-js")) {
        const script = document.createElement("script");
        script.id = "leaflet-js";
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = () => setLeafletReady(true);
        document.head.appendChild(script);
      }
    } else {
      setLeafletReady(true);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const L = (window as any).L;
      if (L && mapContainerRef.current) {
        setLeafletReady(true);
        clearInterval(interval);
      }
    }, 100);
  }, []);

  // Atualizar mapa quando as seleções mudarem
  useEffect(() => {
    if (!leafletReady || !mapContainerRef.current) return;

    const L = (window as any).L;
    if (!L) return;

    // Limpar círculos e marcadores customizados
    if (mapRef.current) {
      mapRef.current.eachLayer((layer: any) => {
        const isCircle = layer instanceof L.Circle;
        const isCustomMarker =
          layer instanceof L.Marker &&
          layer.options.icon &&
          layer.options.icon.options.className === "custom-div-icon";
        if (isCircle || isCustomMarker) {
          layer.remove();
        }
      });
    }

    const icon = L.divIcon({
      className: "custom-div-icon",
      html: `<div style='background-color:#8b5cf6; width:12px; height:12px; border-radius:50%; border:3px solid white; box-shadow:0 0 10px rgba(139,92,246,0.3);'></div>`,
      iconSize: [12, 12],
      iconAnchor: [6, 6],
    });

    if (!mapRef.current) {
      const initialLat = locationMode === "state" ? -14.235 : -23.5505;
      const initialLon = locationMode === "state" ? -51.925 : -46.6333;
      const initialZoom = locationMode === "state" ? 4 : 12;

      mapRef.current = L.map(mapContainerRef.current, {
        center: [initialLat, initialLon],
        zoom: initialZoom,
        zoomControl: false,
        attributionControl: false,
        // On touch devices, dragging & scroll zoom start disabled until user activates
        dragging: isTouchDevice ? false : true,
        scrollWheelZoom: isTouchDevice ? false : true,
        touchZoom: isTouchDevice ? false : true,
        tap: isTouchDevice ? false : true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
        mapRef.current,
      );
      setTimeout(() => mapRef.current?.invalidateSize(), 300);
    }

    // Modo Estado/País
    if (locationMode === "state") {
      if (states.length === 0) {
        // Brasil inteiro
        mapRef.current.setView([-14.235, -51.925], 4);
      } else if (states.length === 1) {
        const st = BR_STATES.find((s) => s.key === states[0]);
        if (st) {
          mapRef.current.setView([st.lat, st.lon], st.zoom);
          L.marker([st.lat, st.lon], { icon }).addTo(mapRef.current);
        }
      } else {
        const bounds = L.latLngBounds([]);
        states.forEach((key) => {
          const st = BR_STATES.find((s) => s.key === key);
          if (st) {
            L.marker([st.lat, st.lon], { icon }).addTo(mapRef.current);
            bounds.extend([st.lat, st.lon]);
          }
        });
        if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds, {
            padding: [60, 60],
            animate: true,
          });
        }
      }
      mapRef.current.invalidateSize();
      return;
    }

    // Modo Raio
    const defaultLoc = { latitude: -23.5505, longitude: -46.6333 };
    const first = customLocations[0] || defaultLoc;
    const startLat = parseFloat(String(first.latitude));
    const startLon = parseFloat(String(first.longitude));

    if (isNaN(startLat) || isNaN(startLon)) return;

    if (customLocations.length === 0) {
      mapRef.current.setView([startLat, startLon], 10);
      mapRef.current.invalidateSize();
      return;
    }

    const bounds = L.latLngBounds([]);

    customLocations.forEach((loc) => {
      const lat = Number(loc.latitude);
      const lon = Number(loc.longitude);
      const currentRadius = Number(loc.radius || radiusKm || 5) * 1000;

      if (isNaN(lat) || isNaN(lon)) return;

      L.circle([lat, lon], {
        color: "#8b5cf6",
        fillColor: "#8b5cf6",
        fillOpacity: 0.2,
        radius: currentRadius,
        weight: 2,
      }).addTo(mapRef.current);

      L.marker([lat, lon], { icon }).addTo(mapRef.current);

      bounds.extend([lat, lon]);
    });

    if (customLocations.length > 1) {
      mapRef.current.fitBounds(bounds, { padding: [50, 50], animate: true });
    } else if (customLocations.length === 1) {
      const lastLoc = customLocations[0];
      const lastLat = Number(lastLoc.latitude);
      const lastLon = Number(lastLoc.longitude);
      const r = Number(lastLoc.radius || radiusKm);
      const zoom = r > 40 ? 9 : r > 15 ? 11 : 13;
      mapRef.current.setView([lastLat, lastLon], zoom);
    }

    mapRef.current.invalidateSize();
  }, [leafletReady, customLocations, radiusKm, locationMode, states]);

  const addLocation = (res: any) => {
    const name = (res.display_name || res.name || t("Localidade")).split(
      ",",
    )[0];
    const lat = res.lat || res.latitude;
    const lon = res.lon || res.longitude;

    if (!lat || !lon) return;

    setCustomLocations([
      ...customLocations,
      {
        latitude: String(lat),
        longitude: String(lon),
        radius: parseInt(radiusKm),
        distance_unit: "kilometer",
        name,
      },
    ]);
    setGeocodeResults([]);
    setSearchTerms("");
  };

  const switchToMode = (mode: "radius" | "state") => {
    setLocationMode(mode);
    if (mode === "state") {
      setCustomLocations([]);
      setTargetBrasil(states.length === 0);
    } else {
      setStates([]);
      setCountry("BR");
      setTargetBrasil(false);
    }
  };

  const toggleState = (key: string) => {
    setTargetBrasil(false);
    if (states.includes(key)) {
      setStates(states.filter((s) => s !== key));
    } else {
      setStates([...states, key]);
    }
  };

  const handleTargetBrasil = () => {
    setTargetBrasil(true);
    setStates([]);
    setCountry("BR");
  };

  // Enable full map interaction on touch devices
  const enableMapInteraction = () => {
    if (!mapRef.current || !isTouchDevice) return;
    mapRef.current.dragging.enable();
    mapRef.current.touchZoom.enable();
    mapRef.current.scrollWheelZoom.enable();
    setMapInteractive(true);
  };

  // Release map interaction when user taps outside the map
  useEffect(() => {
    if (!isTouchDevice) return;
    const handleOutsideTouch = (e: TouchEvent) => {
      if (
        mapContainerRef.current &&
        !mapContainerRef.current.contains(e.target as Node)
      ) {
        if (mapRef.current) {
          mapRef.current.dragging.disable();
          mapRef.current.touchZoom.disable();
          mapRef.current.scrollWheelZoom.disable();
        }
        setMapInteractive(false);
      }
    };
    document.addEventListener("touchstart", handleOutsideTouch, {
      passive: true,
    });
    return () => document.removeEventListener("touchstart", handleOutsideTouch);
  }, [isTouchDevice]);

  const filteredStates = BR_STATES.filter((s) =>
    s.name.toLowerCase().includes(stateFilter.toLowerCase()),
  );

  const selectedNames =
    locationMode === "state"
      ? targetBrasil || states.length === 0
        ? t("BRASIL")
        : states.length === 1
          ? BR_STATES.find((s) => s.key === states[0])?.name || states[0]
          : t("{count} ESTADOS", { count: states.length })
      : customLocations[0]?.name || null;

  const hasSelection =
    locationMode === "state" ? true : customLocations.length > 0;

  // Coordenadas corrompidas (ex: draft antigo/importado) fazem o mapa
  // simplesmente parar de atualizar, sem nenhum indício visual do porquê —
  // avisamos explicitamente em vez de falhar silenciosamente.
  const invalidLocationsCount = customLocations.filter(
    (l: any) =>
      isNaN(parseFloat(String(l.latitude))) ||
      isNaN(parseFloat(String(l.longitude))),
  ).length;

  return (
    <div className="bg-white rounded-2xl border border-purple-200/70 shadow-xs font-sans relative">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Coluna de Inputs */}
        <div className="p-4 md:p-5 space-y-3.5 lg:border-r border-purple-100 flex flex-col bg-white">
          <div className="flex items-center justify-between border-b pb-3 border-purple-100">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-purple-600 rounded-lg text-white shadow-xs">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="h-8 flex flex-col justify-between">
                <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
                  {t("Geolocalização & Raio")}
                </h2>
                <span className="text-[10px] text-gray-400 font-medium leading-none">
                  {locationMode === "state"
                    ? targetBrasil || states.length === 0
                      ? t("Brasil inteiro")
                      : t("{count} estado(s) selecionado(s)", {
                          count: states.length,
                        })
                    : t("{count} local(is) no raio", {
                        count: customLocations.length,
                      })}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs de Modo */}
          <div className="flex bg-purple-50/80 rounded-xl p-0.5 gap-1 border border-purple-100">
            <button
              type="button"
              onClick={() => switchToMode("radius")}
              className={`flex-1 py-1.5 px-3 rounded-lg text-[10px] font-bold uppercase transition-all ${
                locationMode === "radius"
                  ? "bg-white text-purple-700 shadow-xs"
                  : "text-gray-500 hover:text-purple-600"
              }`}
            >
              {t("Endereço / Raio")}
            </button>
            <button
              type="button"
              onClick={() => switchToMode("state")}
              className={`flex-1 py-1.5 px-3 rounded-lg text-[10px] font-bold uppercase transition-all ${
                locationMode === "state"
                  ? "bg-white text-purple-700 shadow-xs"
                  : "text-gray-500 hover:text-purple-600"
              }`}
            >
              {t("Estados / Brasil")}
            </button>
          </div>

          {/* Modo Raio */}
          {locationMode === "radius" && (
            <div className="space-y-3 flex-1">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  {t("Buscar Endereço")}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        async (position) => {
                          const { latitude, longitude } = position.coords;
                          try {
                            const response = await fetch(
                              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
                            );
                            const data = await response.json();
                            const name =
                              data.address.city ||
                              data.address.town ||
                              data.address.suburb ||
                              t("Localização Atual");
                            setCustomLocations([
                              {
                                latitude: String(latitude),
                                longitude: String(longitude),
                                radius: 5,
                                distance_unit: "kilometer",
                                name,
                              },
                            ]);
                          } catch {
                            setCustomLocations([
                              {
                                latitude: String(latitude),
                                longitude: String(longitude),
                                radius: 5,
                                distance_unit: "kilometer",
                                name: t("Minha Localização"),
                              },
                            ]);
                          }
                        },
                      );
                    }
                  }}
                  className="text-[10px] font-bold text-purple-600 uppercase flex items-center gap-1 hover:underline py-1 px-1.5 -my-1 -mr-1.5"
                >
                  <Crosshair className="w-3 h-3" />
                  {t("Usar meu GPS")}
                </button>
              </div>

              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("Ex: Av. Paulista, São Paulo...")}
                  value={searchTerms}
                  onChange={(e) => setSearchTerms(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGeocodeSearch()}
                  className="w-full h-10 pl-10 pr-4 bg-purple-50/30 border border-purple-200 rounded-xl text-xs font-medium placeholder:text-gray-400 outline-hidden focus:border-purple-600 focus:ring-2 focus:ring-purple-500/20 transition-all"
                />
                {geocodeResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-purple-100 rounded-xl overflow-y-auto max-h-48 shadow-lg z-100">
                    {geocodeResults.slice(0, 5).map((res, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => addLocation(res)}
                        className="w-full p-2.5 text-left hover:bg-purple-50 transition-all border-b border-purple-50 last:border-0 flex items-start gap-2"
                      >
                        <Globe className="w-3.5 h-3.5 text-purple-400 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-gray-800 truncate">
                            {res.display_name.split(",")[0]}
                          </div>
                          <div className="text-[10px] text-gray-400 truncate">
                            {res.display_name}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {invalidLocationsCount > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-[10px] font-bold text-amber-700">
                  <AlertTriangle className="w-3 h-3 shrink-0" />
                  {t(
                    "{count} localização(ões) com coordenadas inválidas — não aparecem no mapa.",
                    { count: invalidLocationsCount },
                  )}
                </div>
              )}

              <div className="space-y-1.5 max-h-28 overflow-y-auto pr-1">
                {customLocations.map((loc, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 bg-purple-50/40 border border-purple-100 rounded-lg"
                  >
                    <Crosshair className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-gray-900 truncate">
                        {loc.name}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <input
                          type="range"
                          min="1"
                          max="80"
                          value={loc.radius}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setCustomLocations(
                              customLocations.map((l, idx) =>
                                idx === i ? { ...l, radius: val } : l,
                              ),
                            );
                          }}
                          className="flex-1 h-1 bg-purple-200 rounded-full appearance-none cursor-pointer accent-purple-600"
                        />
                        <span className="text-[10px] font-bold text-purple-600 w-10 text-right shrink-0">
                          {loc.radius} KM
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setCustomLocations(
                          customLocations.filter((_, idx) => idx !== i),
                        )
                      }
                      className="text-gray-400 hover:text-red-500 p-2 -m-1 shrink-0"
                      aria-label={t("Remover {name}", { name: loc.name })}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="pt-2 border-t border-purple-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-gray-700 uppercase">
                    {t("Raio Geral")}
                  </span>
                  <span className="text-xs font-bold text-purple-600">
                    {radiusKm} KM
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="80"
                  value={radiusKm}
                  onChange={(e) => {
                    const val = String(e.target.value);
                    setRadiusKm(val);
                    if (customLocations.length > 0) {
                      setCustomLocations(
                        customLocations.map((l) => ({
                          ...l,
                          radius: parseInt(val),
                        })),
                      );
                    }
                  }}
                  className="w-full h-1.5 bg-purple-100 rounded-full appearance-none cursor-pointer accent-purple-600"
                />
              </div>
            </div>
          )}

          {/* Modo Estado ou País */}
          {locationMode === "state" && (
            <div className="space-y-2.5 flex-1 flex flex-col">
              <button
                type="button"
                onClick={handleTargetBrasil}
                className={`flex items-center gap-2.5 p-2.5 rounded-xl border transition-all text-left ${
                  targetBrasil || states.length === 0
                    ? "border-purple-600 bg-purple-50/50 shadow-xs"
                    : "border-gray-200 hover:border-purple-200"
                }`}
              >
                {targetBrasil || states.length === 0 ? (
                  <CheckSquare className="w-4 h-4 text-purple-600 shrink-0" />
                ) : (
                  <Square className="w-4 h-4 text-gray-300 shrink-0" />
                )}
                <div>
                  <div className="text-xs font-bold text-gray-900">
                    {t("Brasil inteiro")}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {t("Todo o território nacional")}
                  </div>
                </div>
              </button>

              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("Filtrar estados...")}
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 bg-purple-50/30 border border-purple-200 rounded-xl text-xs font-medium placeholder:text-gray-400 outline-hidden focus:border-purple-600 focus:ring-2 focus:ring-purple-500/20 transition-all"
                />
              </div>

              <div className="flex-1 overflow-y-auto max-h-36 space-y-0.5 pr-1">
                {filteredStates.map((st) => {
                  const isSelected = states.includes(st.key);
                  return (
                    <button
                      key={st.key}
                      type="button"
                      onClick={() => toggleState(st.key)}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-all text-xs ${
                        isSelected
                          ? "bg-purple-600 text-white font-bold"
                          : "hover:bg-purple-50 text-gray-700"
                      }`}
                    >
                      {isSelected ? (
                        <CheckSquare className="w-3.5 h-3.5 shrink-0" />
                      ) : (
                        <Square className="w-3.5 h-3.5 shrink-0 opacity-40" />
                      )}
                      <span className="truncate">{st.name}</span>
                      <span
                        className={`ml-auto text-[10px] ${isSelected ? "text-purple-100" : "text-gray-400"}`}
                      >
                        {st.key.replace("BR-", "")}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Coluna do Mapa */}
        <div className="relative h-56 lg:h-auto lg:min-h-[280px] bg-slate-50 overflow-hidden rounded-b-2xl lg:rounded-bl-none lg:rounded-r-2xl group/map">
          <div
            ref={mapContainerRef}
            className="absolute inset-0 z-0 w-full h-full"
            style={{ background: "#f8f9fa" }}
          />

          <div className="absolute inset-0 bg-linear-to-br from-purple-900/5 via-transparent to-transparent pointer-events-none z-10" />

          {hasSelection && (
            <div className="absolute bottom-3 right-3 z-30">
              <div className="bg-white/95 backdrop-blur-md border border-purple-100 rounded-xl px-3 py-1.5 shadow-md flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-[10px] font-bold text-gray-800 uppercase">
                  {selectedNames}
                </span>
              </div>
            </div>
          )}

          {locationMode === "radius" && customLocations.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 space-y-2 z-20 bg-white/40 backdrop-blur-[2px]">
              <Globe className="w-8 h-8 text-purple-300" />
              <div className="text-xs font-bold text-gray-600">
                {t("Área de Cobertura")}
              </div>
              <p className="text-[10px] text-gray-400 max-w-[200px]">
                {t(
                  "Adicione um local ou selecione estados para visualizar no mapa.",
                )}
              </p>
            </div>
          )}

          {/* Touch overlay: shown on mobile/touch when map is in scroll-safe mode */}
          {isTouchDevice && !mapInteractive && (
            <button
              type="button"
              onClick={enableMapInteraction}
              className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-black/20 backdrop-blur-[1px] transition-opacity duration-200"
              aria-label={t("Toque para interagir com o mapa")}
            >
              <div className="bg-white/90 backdrop-blur-md rounded-2xl px-4 py-2.5 shadow-lg flex items-center gap-2.5 border border-white/60">
                <span className="text-lg leading-none select-none">👆</span>
                <span className="text-xs font-bold text-gray-800">
                  {t("Toque para interagir com o mapa")}
                </span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeolocationCenter;
